import { useContext, useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { RangeSlider } from '@mantine/core';
import { BedethequeContext } from '../utils/context/bedetheque'
import HeaderPage from '../components/headerPage';
import { getPhrasedujour } from '../libs/utilitaires.jsx'
import '../utils/style/stats.css';

import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Palette maison (vert + rose + accents) — surtout pas le marron de la maquette Stitch.
const ACCENTS = ['#f44b70', '#69DB7C', '#ffd43b', '#4dabf7', '#b197fc', '#ff922b', '#38d9a9', '#f783ac', '#a9e34b', '#74c0fc'];

// Métadonnées des cartes récap (clé = _id renvoyé par le bloc DIVERS du backend).
const CARD_META = {
    albums:     { label: 'Albums',   icon: '📚', color: '#f44b70' },
    'séries':   { label: 'Séries',   icon: '📖', color: '#69DB7C' },
    'éditeurs': { label: 'Éditeurs', icon: '🏢', color: '#ffd43b' },
    auteurs:    { label: 'Auteurs',  icon: '✍️', color: '#4dabf7' },
    genres:     { label: 'Genres',   icon: '🏷️', color: '#b197fc' },
};

// Lien cliquable lançant une recherche (auteur / éditeur / genre / note) — pattern `../search/<terme>`.
const SearchLink = ({ q, children }) => (
    <Link className="stats-link" to={`../search/${q}`}>{children}</Link>
);

const StatCard = ({ id, count }) => {
    const meta = CARD_META[id] || { label: id, icon: '📊', color: '#f44b70' };
    return (
        <div className="stat-card" style={{ '--accent': meta.color }}>
            <div className="stat-card-head">
                <span className="stat-card-label">{meta.label}</span>
                <span className="stat-card-icon">{meta.icon}</span>
            </div>
            <div className="stat-card-value">{count}</div>
            <div className="stat-card-bar"><span /></div>
        </div>
    );
};

// Liste de barres horizontales (réutilisée pour Top Éditeurs, Top Auteurs, Notes).
const BarList = ({ items, max, unit = 'albums', color = '#69DB7C' }) => (
    <div className="bar-list">
        {items.map((it, i) => (
            <div className="bar-row" key={`${it._id}-${i}`}>
                <div className="bar-row-head">
                    <span className="bar-row-name"><SearchLink q={it._id}>{it._id}</SearchLink></span>
                    <span className="bar-row-count">{it.count}{unit ? ` ${unit}` : ''}</span>
                </div>
                <div className="bar-track">
                    <span className="bar-fill" style={{ width: `${max ? (it.count / max) * 100 : 0}%`, background: color }} />
                </div>
            </div>
        ))}
    </div>
);

// Podium des notes : échelle fixe 0→5, hauteur proportionnelle, nombre au-dessus.
const NotePodium = ({ notes }) => {
    const byNote = Object.fromEntries(notes.map((n) => [parseInt(n._id), n.count]));
    const scale = [0, 1, 2, 3, 4, 5].map((v) => ({ note: v, count: byNote[v] || 0 }));
    const max = Math.max(...scale.map((s) => s.count), 1);
    return (
        <div className="note-podium">
            {scale.map((s) => (
                <div className="note-col" key={s.note}>
                    <span className="note-count">{s.count}</span>
                    <div className="note-track">
                        <div className="note-bar" style={{ height: `${(s.count / max) * 100}%` }} />
                    </div>
                    <span className="note-label"><SearchLink q={`note${s.note}`}>{s.note} ★</SearchLink></span>
                </div>
            ))}
        </div>
    );
};

const GenreCard = ({ genre, count, total, color }) => {
    const pct = total ? Math.round((count / total) * 100) : 0;
    return (
        <div className="genre-card" style={{ '--accent': color }}>
            <span className="genre-pct">{pct}%</span>
            <div className="genre-name"><SearchLink q={genre}>{genre}</SearchLink></div>
            <div className="genre-count">{count} albums</div>
        </div>
    );
};

const Stats = () => {
    const { stats } = useContext(BedethequeContext)
    const navigate = useNavigate();
    const [phraseDuJour, setPhraseDuJour] = useState(false);
    const [range, setRange] = useState(null); // [annéeDébut, annéeFin] sélectionnées

    useEffect(() => {
        setPhraseDuJour(getPhrasedujour(stats));
    }, [stats]);

    // Initialise la plage sur les bornes réelles des données (une seule fois).
    useEffect(() => {
        if (!stats) return;
        const years = (stats.find((b) => b.TYPESTATS === 'PARUTION')?.STATS || [])
            .map((d) => parseInt(d._id)).filter((y) => !isNaN(y));
        if (years.length) {
            const lo = Math.min(...years), hi = Math.max(...years);
            setRange((prev) => prev || [lo, hi]);
        }
    }, [stats]);

    if (!stats) return null;

    // Indexe les blocs renvoyés par l'API par TYPESTATS pour un accès direct.
    const byType = Object.fromEntries(stats.map((b) => [b.TYPESTATS, b.STATS || []]));
    const divers = byType.DIVERS || [];
    const totalAlbums = divers.find((d) => d._id === 'albums')?.count || 0;

    const editeurs = [...(byType.EDITEUR || [])].sort((a, b) => b.count - a.count).slice(0, 8);
    const auteurs = [...(byType.AUTEUR || [])].sort((a, b) => b.count - a.count).slice(0, 8);
    const genres = [...(byType.GENRE || [])].sort((a, b) => b.count - a.count).slice(0, 6);
    const notes = [...(byType.NOTE || [])].sort((a, b) => parseInt(a._id) - parseInt(b._id));

    // Graphe par année de parution, cadré par le curseur de plage [début, fin].
    const parution = [...(byType.PARUTION || [])]
        .map((d) => ({ year: parseInt(d._id), count: d.count }))
        .filter((d) => !isNaN(d.year))
        .sort((a, b) => a.year - b.year);
    const minYear = parution.length ? parution[0].year : 0;
    const maxYear = parution.length ? parution[parution.length - 1].year : 0;
    const [from, to] = range || [minYear, maxYear];
    const parutionShown = parution.filter((d) => d.year >= from && d.year <= to);
    const isFullRange = from <= minYear && to >= maxYear;
    const selectedTotal = parutionShown.reduce((sum, d) => sum + d.count, 0);

    // Repères par décennie sur le curseur.
    const yearMarks = [];
    for (let y = Math.ceil(minYear / 10) * 10; y <= maxYear; y += 10) yearMarks.push({ value: y, label: `${y}` });

    const chartData = {
        labels: parutionShown.map((d) => d.year),
        datasets: [{
            data: parutionShown.map((d) => d.count),
            // La dernière année (la plus récente) ressort en vert, le reste en rose.
            backgroundColor: parutionShown.map((_, i) => (i === parutionShown.length - 1 ? '#69DB7C' : 'rgba(244,75,112,0.75)')),
            hoverBackgroundColor: '#69DB7C',
            borderRadius: 6,
            borderSkipped: false,
            maxBarThickness: 64,
        }],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        // Clic sur une barre → recherche des albums de cette année de parution.
        onClick: (_evt, elements) => {
            if (elements.length) navigate(`../search/${parutionShown[elements[0].index].year}`);
        },
        onHover: (evt, elements) => {
            evt.native.target.style.cursor = elements.length ? 'pointer' : 'default';
        },
        plugins: {
            legend: { display: false },
            tooltip: { backgroundColor: '#1a1717', titleColor: '#fff', bodyColor: '#f3eaea', padding: 10, displayColors: false },
        },
        scales: {
            x: { ticks: { color: '#c9b8b8', font: { size: 12 } }, grid: { display: false } },
            y: { ticks: { color: '#c9b8b8', font: { size: 12 } }, grid: { color: 'rgba(255,255,255,0.06)' }, beginAtZero: true },
        },
    };

    return (
        <div>
            {phraseDuJour &&
                <HeaderPage leftText={phraseDuJour} centerText={'Statistiques'} rightText={''} />
            }
            <div className="stats-page">
                <div className="stats-cards">
                    {divers.map((d) => <StatCard key={d._id} id={d._id} count={d.count} />)}
                </div>

                <section className="stats-panel">
                    <div className="stats-panel-head">
                        <h2 className="stats-title">Albums par année de parution</h2>
                        <span className="stats-range-label">
                            {from} – {to}
                            {!isFullRange && <span className="stats-range-total">{selectedTotal} albums</span>}
                        </span>
                    </div>
                    {minYear !== maxYear &&
                        <div className="stats-range">
                            <RangeSlider
                                min={minYear}
                                max={maxYear}
                                step={1}
                                minRange={1}
                                value={[from, to]}
                                onChange={setRange}
                                marks={yearMarks}
                                label={(v) => v}
                                color="pink"
                                size="sm"
                            />
                        </div>
                    }
                    <div className="stats-chart"><Bar data={chartData} options={chartOptions} /></div>
                </section>

                <div className="stats-grid">
                    <section className="stats-panel">
                        <h2 className="stats-title">Top des Genres</h2>
                        <div className="genre-grid">
                            {genres.map((g, i) => (
                                <GenreCard key={g._id} genre={g._id} count={g.count} total={totalAlbums} color={ACCENTS[i % ACCENTS.length]} />
                            ))}
                        </div>
                    </section>

                    <section className="stats-panel">
                        <h2 className="stats-title">Répartition des notes</h2>
                        <NotePodium notes={notes} />
                    </section>

                    <section className="stats-panel">
                        <h2 className="stats-title">Top des Éditeurs</h2>
                        <BarList items={editeurs} max={editeurs[0]?.count} color="#69DB7C" />
                    </section>

                    <section className="stats-panel">
                        <h2 className="stats-title">Top des Auteurs</h2>
                        <BarList items={auteurs} max={auteurs[0]?.count} color="#f44b70" />
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Stats;
